import type { HeartbeatSemanticRejection } from "./airshowHeartbeatSemanticRejections";

export const HEARTBEAT_CATALOG_ID = "openvsp-airshow-complete-2026-08-13" as const;
export const HEARTBEAT_TARGETS = [
  "typed", "semantic-complete", "renderable", "exact-attested",
] as const;
export const HEARTBEAT_LANES = [
  "archive-source", "source-audit", "semantic", "exact", "display", "reference",
] as const;
export const HEARTBEAT_TERMINAL_STATES = [
  "completed", "cancelled", "failed", "interrupted",
] as const;
export const HEARTBEAT_REPORT_FILENAME = "openvsp-airshow-heartbeat-report.json";

export type HeartbeatTarget = typeof HEARTBEAT_TARGETS[number];
export type HeartbeatLane = typeof HEARTBEAT_LANES[number];
export type HeartbeatRunState = "queued" | "running" | "cancelling"
  | typeof HEARTBEAT_TERMINAL_STATES[number];
export type HeartbeatLaneStatus = "passed" | "failed" | "unavailable" | "skipped";
export type HeartbeatTargetStatus = "passed" | "failed" | "not-run";

export interface HeartbeatCatalogIdentity {
  catalog_id: typeof HEARTBEAT_CATALOG_ID;
  catalog_revision: string;
  archive_sha256: string;
}
export interface HeartbeatCurrentModel { ordinal: number; entry_id: string }
export interface HeartbeatRunError { code: string; message: string; retryable: boolean }
export interface AirshowHeartbeatRun {
  schema: "loom.airshow-heartbeat-run/v1";
  run_id: string;
  project_id: string;
  catalog: HeartbeatCatalogIdentity;
  scope: "all" | "failures-from";
  prior_run_id: string | null;
  targets: readonly HeartbeatTarget[];
  state: HeartbeatRunState;
  created_at: string;
  started_at: string | null;
  updated_at: string;
  last_pulse_at: string | null;
  finished_at: string | null;
  total_models: number;
  processed_models: number;
  succeeded_models: number;
  failed_models: number;
  event_sequence: number;
  last_event_digest: string | null;
  report_revision: string | null;
  cancel_requested: boolean;
  pulse_stale: boolean;
  current_model: HeartbeatCurrentModel | null;
  error: HeartbeatRunError | null;
}
export interface HeartbeatLaneMetrics {
  component_count: number | null;
  supported_component_count: number | null;
  skipped_component_count: number | null;
  source_component_count: number | null;
  realized_component_count: number | null;
  source_surface_count: number | null;
  realized_surface_count: number | null;
  skipped_surface_count: number | null;
  unknown_component_types: readonly string[];
  unknown_xsec_types: readonly number[];
  rejected_feature_ids: readonly string[];
  unsupported_declarations: readonly string[];
  semantic_rejections: readonly HeartbeatSemanticRejection[];
  representation: string | null;
  fidelity: string | null;
  geometry_complete: boolean | null;
  export_order_attested: boolean | null;
  artifact_sha256: string | null;
  authoring_receipt_revision?: string;
  aircraft_ir_revision?: string | null;
  aircraft_topology_revision?: string | null;
  hierarchy_receipt_revision?: string | null;
  source_occurrence_count?: number;
  represented_occurrence_count?: number;
  policy_excluded_occurrence_count?: number;
  control_occurrence_count?: number;
  represented_control_count?: number;
  rejected_control_count?: number;
  unresolved_control_count?: number;
  control_rejection_codes?: readonly string[];
  propulsor_occurrence_count?: number;
  represented_propulsor_count?: number;
  rejected_propulsor_count?: number;
  unresolved_propulsor_count?: number;
  propulsor_rejection_codes?: readonly string[];
  body_representation_kinds?: readonly ("adapted" | "exact-attested" | "native-triangle")[];
}
export interface HeartbeatLaneResult {
  lane: HeartbeatLane; status: HeartbeatLaneStatus; code: string; message: string;
  duration_ms: number; metrics: HeartbeatLaneMetrics;
}
export interface HeartbeatTargetResult {
  target: HeartbeatTarget; status: HeartbeatTargetStatus; code: string; message: string;
}
export interface HeartbeatModelResult {
  schema: "loom.airshow-heartbeat-model/v1";
  ordinal: number; entry_id: string; archive_member_path: string; source_sha256: string | null;
  started_at: string; finished_at: string; duration_ms: number;
  lanes: readonly HeartbeatLaneResult[]; targets: readonly HeartbeatTargetResult[];
  cluster_ids: readonly string[];
}
export interface HeartbeatSelectedModel { entry_id: string; archive_member_path: string }
export interface HeartbeatTargetSummary {
  target: HeartbeatTarget; passed: number; failed: number; not_processed: number;
}
export interface HeartbeatFailureCluster {
  cluster_id: string; lane: HeartbeatLane; code: string;
  feature_declarations: readonly string[]; model_entry_ids: readonly string[]; count: number;
  remedy: {
    kind: "archive-integrity" | "source-parser" | "semantic-primitive" | "exact-control"
      | "display-export" | "historical-reference";
    summary: string; automatic: false;
  };
}
export interface AirshowHeartbeatReport {
  schema: "loom.airshow-heartbeat-report/v1";
  report_revision: string;
  generated_at: string;
  run: AirshowHeartbeatRun;
  selected_models: readonly HeartbeatSelectedModel[];
  summary: {
    processed_models: number; succeeded_models: number; failed_models: number;
    unprocessed_models: number; cluster_count: number;
    targets: readonly HeartbeatTargetSummary[];
  };
  models: readonly HeartbeatModelResult[];
  clusters: readonly HeartbeatFailureCluster[];
}
export interface AirshowHeartbeatModelsPage {
  schema: "loom.airshow-heartbeat-model-page/v1";
  run_id: string; project_id: string; cursor: number; limit: number;
  next_cursor: number | null; total: number; items: readonly HeartbeatModelResult[];
}
